"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";

/** English strings. A translated content file overrides these through `ui`. */
export const RATIO_CALCULATOR_STRINGS = {
  simplifyHeading: "Simplify a ratio A:B",
  labelA: "A",
  labelB: "B",
  simplifyInvalid: "Enter two positive whole numbers to simplify the ratio.",
  headlineColon: " : ",
  headlineEquals: " = ",
  gcdPrefix: "GCD(",
  gcdMid: ", ",
  gcdSuffix: ") = ",
  divideEquals: " = ",
  divideSign: " ÷ ",
  simplifiedRatio: "Simplified ratio: {a} : {b}",
  equivalentHeading: "Equivalent ratios — scale A:B by a multiplier",
  multiplierLabel: "Multiplier (k)",
  equivalentInvalid:
    "Enter A, B, and a positive multiplier to see equivalent ratios.",
  multiplySign: " × ",
  bothSides: "Both sides multiplied by {k}, so the ratio stays the same.",
  multiplierColumn: "Multiplier",
  equivalentRatioColumn: "Equivalent ratio",
  multiplierPrefix: "×",
  proportionHeading: "Solve a proportion A:B = C:x",
  labelC: "C",
  proportionInvalid: "Enter A, B, and C (all positive) to solve for x.",
  xEqualsPrefix: "x = ",
  proportionColonX: " : ",
  proportionEqualsColonX: " = ",
  colonX: " : x",
  crossMultiply: "Cross-multiply: {a} × x = {b} × {c}",
  xEqualsMultiply: " × x = ",
} as const;

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

function parsePositiveInt(value: string): number | null {
  const n = parseIntegerInput(value);
  if (n === null || n <= 0) {
    return null;
  }
  return n;
}

function parsePositiveNum(value: string): number | null {
  const n = parseNumberInput(value);
  if (n === null || n <= 0) {
    return null;
  }
  return n;
}

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

function fmt(n: number, formatNum: NumberFormatter): string {
  const rounded = Math.round(n * 10_000) / 10_000;
  return formatNum(rounded, { maximumFractionDigits: 4 });
}

function NumberField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <label className="block font-medium text-foreground text-sm" htmlFor={id}>
        {label}
      </label>
      <input
        className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        id={id}
        onChange={(e) => onChange(e.target.value)}
        step="any"
        type="number"
        value={value}
      />
    </div>
  );
}

function ResultBlock({
  headline,
  working,
}: {
  headline: string;
  working: string[];
}) {
  return (
    <div className="space-y-2">
      <p className="font-semibold text-2xl text-foreground">{headline}</p>
      <div className="space-y-1 rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm leading-relaxed">
        {working.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
    </div>
  );
}

function SimplifyRatioSection({
  strings,
}: {
  strings: typeof RATIO_CALCULATOR_STRINGS;
}) {
  const [a, setA] = useState("12");
  const [b, setB] = useState("18");
  const formatNum = useToolNumber();
  const av = parsePositiveInt(a);
  const bv = parsePositiveInt(b);
  const valid = av !== null && bv !== null;
  const g = valid ? gcd(av, bv) : 1;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.simplifyHeading}
      </h2>
      <div className="flex flex-wrap items-end gap-6">
        <NumberField
          id="simp-a"
          label={strings.labelA}
          onChange={setA}
          value={a}
        />
        <NumberField
          id="simp-b"
          label={strings.labelB}
          onChange={setB}
          value={b}
        />
      </div>
      {valid && (
        <ResultBlock
          headline={`${fmt(av, formatNum)}${strings.headlineColon}${fmt(bv, formatNum)}${strings.headlineEquals}${fmt(av / g, formatNum)}${strings.headlineColon}${fmt(bv / g, formatNum)}`}
          working={[
            `${strings.gcdPrefix}${fmt(av, formatNum)}${strings.gcdMid}${fmt(bv, formatNum)}${strings.gcdSuffix}${fmt(g, formatNum)}`,
            `${fmt(av, formatNum)}${strings.divideSign}${fmt(g, formatNum)}${strings.divideEquals}${fmt(av / g, formatNum)}`,
            `${fmt(bv, formatNum)}${strings.divideSign}${fmt(g, formatNum)}${strings.divideEquals}${fmt(bv / g, formatNum)}`,
            t(strings.simplifiedRatio, {
              a: fmt(av / g, formatNum),
              b: fmt(bv / g, formatNum),
            }),
          ]}
        />
      )}
      {!valid && (
        <p className="text-muted-foreground text-sm">
          {strings.simplifyInvalid}
        </p>
      )}
    </section>
  );
}

const EQUIVALENT_MULTIPLIERS = [2, 3, 4, 10];

function EquivalentRatioSection({
  strings,
}: {
  strings: typeof RATIO_CALCULATOR_STRINGS;
}) {
  const [a, setA] = useState("2");
  const [b, setB] = useState("3");
  const [k, setK] = useState("5");
  const formatNum = useToolNumber();
  const av = parsePositiveNum(a);
  const bv = parsePositiveNum(b);
  const kv = parsePositiveNum(k);
  const valid = av !== null && bv !== null && kv !== null;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.equivalentHeading}
      </h2>
      <div className="flex flex-wrap items-end gap-6">
        <NumberField
          id="eq-a"
          label={strings.labelA}
          onChange={setA}
          value={a}
        />
        <NumberField
          id="eq-b"
          label={strings.labelB}
          onChange={setB}
          value={b}
        />
        <NumberField
          id="eq-k"
          label={strings.multiplierLabel}
          onChange={setK}
          value={k}
        />
      </div>
      {valid && (
        <>
          <ResultBlock
            headline={`${fmt(av, formatNum)}${strings.headlineColon}${fmt(bv, formatNum)}${strings.headlineEquals}${fmt(av * kv, formatNum)}${strings.headlineColon}${fmt(bv * kv, formatNum)}`}
            working={[
              `${fmt(av, formatNum)}${strings.multiplySign}${fmt(kv, formatNum)}${strings.divideEquals}${fmt(av * kv, formatNum)}`,
              `${fmt(bv, formatNum)}${strings.multiplySign}${fmt(kv, formatNum)}${strings.divideEquals}${fmt(bv * kv, formatNum)}`,
              t(strings.bothSides, { k: fmt(kv, formatNum) }),
            ]}
          />
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-border border-b text-left">
                  <th className="py-2 pr-4 font-medium text-foreground">
                    {strings.multiplierColumn}
                  </th>
                  <th className="py-2 font-medium text-foreground">
                    {strings.equivalentRatioColumn}
                  </th>
                </tr>
              </thead>
              <tbody>
                {EQUIVALENT_MULTIPLIERS.map((m) => (
                  <tr className="border-border border-b" key={m}>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {strings.multiplierPrefix}
                      {m}
                    </td>
                    <td className="py-2 font-mono text-foreground">
                      {fmt(av * m, formatNum)} : {fmt(bv * m, formatNum)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!valid && (
        <p className="text-muted-foreground text-sm">
          {strings.equivalentInvalid}
        </p>
      )}
    </section>
  );
}

function ProportionSection({
  strings,
}: {
  strings: typeof RATIO_CALCULATOR_STRINGS;
}) {
  const [a, setA] = useState("2");
  const [b, setB] = useState("3");
  const [c, setC] = useState("8");
  const formatNum = useToolNumber();
  const av = parsePositiveNum(a);
  const bv = parsePositiveNum(b);
  const cv = parsePositiveNum(c);
  const valid = av !== null && bv !== null && cv !== null;
  const x = valid ? (bv * cv) / av : 0;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.proportionHeading}
      </h2>
      <div className="flex flex-wrap items-end gap-6">
        <NumberField
          id="prop-a"
          label={strings.labelA}
          onChange={setA}
          value={a}
        />
        <NumberField
          id="prop-b"
          label={strings.labelB}
          onChange={setB}
          value={b}
        />
        <NumberField
          id="prop-c"
          label={strings.labelC}
          onChange={setC}
          value={c}
        />
      </div>
      {valid && (
        <ResultBlock
          headline={`${strings.xEqualsPrefix}${fmt(x, formatNum)}`}
          working={[
            `${fmt(av, formatNum)}${strings.proportionColonX}${fmt(bv, formatNum)}${strings.proportionEqualsColonX}${fmt(cv, formatNum)}${strings.colonX}`,
            t(strings.crossMultiply, {
              a: fmt(av, formatNum),
              b: fmt(bv, formatNum),
              c: fmt(cv, formatNum),
            }),
            `${fmt(av, formatNum)}${strings.xEqualsMultiply}${fmt(bv * cv, formatNum)}`,
            `${strings.xEqualsPrefix}${fmt(bv * cv, formatNum)}${strings.divideSign}${fmt(av, formatNum)}${strings.divideEquals}${fmt(x, formatNum)}`,
          ]}
        />
      )}
      {!valid && (
        <p className="text-muted-foreground text-sm">
          {strings.proportionInvalid}
        </p>
      )}
    </section>
  );
}

export default function RatioCalculatorWidget() {
  const strings = useToolUi(RATIO_CALCULATOR_STRINGS);
  return (
    <>
      <SimplifyRatioSection strings={strings} />
      <EquivalentRatioSection strings={strings} />
      <ProportionSection strings={strings} />
    </>
  );
}
